import { NIMGetInstanceOptions } from '@yxim/nim-web-sdk/dist/SDK/NIM_Web_NIM/types';
import nimitt from './nimitt';

export const NIM_LOGIN_SIGNAL = Symbol('NIM_LOGIN_SIGNAL');

export type InstanceOptions = NIMGetInstanceOptions & { orgId: number; userId: number };

export type Events =
  | 'message'
  | 'connect'
  | 'disconnect'
  | 'error'
  | 'willReconnect'
  | `sys:message:${Lowercase<NimSystemMessageType>}`;
type Off = () => void;

export type NimSystemMessageType =
  | 'HEALTH_ACK'
  | 'ACK'
  | 'ACK_TIMEOUT'
  | 'ASSIGN_FAIL'
  | 'UNREAD_COUNT'
  | 'READ_EVENT'
  | 'FOLLOWER_EVENT'
  | 'AI_REPLY'
  | 'OFFICIAL_READ_MARK'
  | 'ASSISTANT_READ_MARK'
  | 'REMIND_SCENE'
  | 'REMIND_SOLVE'
  | 'STORE_TRANSFER'
  | 'MESSAGE_EVENT_STREAM'
  | 'REMOVE_TALK_CONTROL'
  | 'PAY'
  | 'PULL_NOTICE_MSG'
  | 'SYS_REMIND_TRIGGER'
  | 'SYS_REMIND_MANUAL_CLOSE'
  | 'SYS_REMIND_AUTO_CLOSE'
  | 'SYS_WA_REJECT'
  | 'SYS_ASTERISK_STAR'
  | 'RECALL'
  | 'SYNC_TIKTOK_TRADE_UID'
  | 'BATCH_TRANSFER'
  | 'SEND_MSG_REQ'
  | 'BOT_SENT_LIMIT_REACH'
  | 'ASSIGN_CONVERSATION_STORES_URL'
  | 'SYS_MANUAL_MARK_SALES_STATUS'
  | 'SYS_AUTO_MARK_SALES_STATUS'
  | 'SYNC_NOTICE_UNREAD_COUNT'
  | 'EXPORT_FILE'
  | 'SYNC_SHOP_INFO'
  | 'TIKTOK_SHOP_OFFLINE'
  | 'UPDATE_MARK'
  | 'AUTH_SUCCESS'
  | 'KNOWLEDGE_QUERY_CLEAR'
  | 'KNOWLEDGE_QUERY';

export interface NimSystemBasicMessage {
  type: NimSystemMessageType;
  content: any;
}

export interface NimSystemACKMessage extends NimSystemBasicMessage {
  type: 'ACK';
  content: {
    msgId: number;
    yunXinMsgId: Conversation.Message['yunXinMsgId'];
    success: boolean;
    failCause?: Conversation.FailContent;
  };
}

export interface NimSystemHealthACKMessage extends NimSystemBasicMessage {
  uuid: string;
}

export interface NimSystemACKTimeoutMessage extends NimSystemBasicMessage {
  type: 'ACK_TIMEOUT';
  content: {
    // msgId: number;
    yunXinMsgId: Conversation.Message['yunXinMsgId'];
    message: 'timeout';
  };
}

export interface NimSytemAssignFailMessage extends NimSystemBasicMessage {
  type: 'ASSIGN_FAIL';
  content: {
    conversationId: string;
  };
}

export interface NimSystemRemoveTalkControlMessage extends NimSystemBasicMessage {
  type: 'REMOVE_TALK_CONTROL';
  content: {
    conversationId: string;
    talkId: string;
    userId: string;
  };
}

export interface NimSystemEventStreamMessage extends NimSystemBasicMessage {
  type: 'MESSAGE_EVENT_STREAM';
  content: {
    externalStoreId: string;

    platformType: Review.Platform;

    region: General.UpperCaseRegion;

    externalBuyerId: string;

    buyerNick: string;

    eventType: 'RETURN_ORDER' | 'IN_CANCEL_ORDER' | 'COMMENT';
    content: string;
  };
}

export type ReadStatus = 'mark_as_read' | 'mark_as_unread';

export interface NimSytemUnreadCountMessage extends NimSystemBasicMessage {
  type: 'UNREAD_COUNT';
  content: {
    conversationId: string;
    status: ReadStatus;
    count: number;
    readTime: number;
    talkId: string;
  };
}

export interface NimSystemReadEventMessage extends NimSystemBasicMessage {
  type: 'READ_EVENT';
  content: {
    conversationId: number;
    readTime: number;
  };
}

export interface NimSystemFollowerEventMessage extends NimSystemBasicMessage {
  type: 'FOLLOWER_EVENT';
  content: {
    conversationId: number;
    follower: boolean;
  };
}

export interface NimSystemAIReplyMessage extends NimSystemBasicMessage {
  type: 'AI_REPLY';
  content: Conversation.AIReply.QueryReply;
}

export interface NimSystemOfficialReadMarkMessage extends NimSystemBasicMessage {
  type: 'OFFICIAL_READ_MARK';
  content: {
    conversationId: number;
    status: ReadStatus;
    timestamp: number;
  };
}

export interface NimSystemAssistantReadMarkMessage extends NimSystemBasicMessage {
  type: 'ASSISTANT_READ_MARK';
  content: {
    conversationId: number;
    talkId: number;
    status: ReadStatus;
    timestamp: number;
  };
}

export interface NimSystemRemindSceneMessage extends NimSystemBasicMessage {
  type: 'REMIND_SCENE';
  content: {
    conversationId: number;
    scene: string;
    hint: {
      zhCN: string;
      enUS: string;
    };
    timestamp: number;
  };
}

export interface NimSystemRemindSolveMessage extends NimSystemBasicMessage {
  type: 'REMIND_SOLVE';
  content: {
    conversationId: number;
    timestamp: number;
    processed: boolean;
  };
}

export interface NimSystemStoreTransferMessage extends NimSystemBasicMessage {
  type: 'STORE_TRANSFER';
}

export interface NimSystemPayMessage extends NimSystemBasicMessage {
  type: 'PAY';
  content: {
    result: 'success' | 'cancel' | 'fail';
    orderCode: string;
  };
}

export interface NimSystemBatchTransferMessage extends NimSystemBasicMessage {
  type: 'BATCH_TRANSFER';
  content: {
    success: boolean;
    failItem: Array<{ conversationId: number; code: number; msg: string }>;
  };
}

export interface NimSystemSyncTiktokTradeUidMessage extends NimSystemBasicMessage {
  type: 'SYNC_TIKTOK_TRADE_UID';
  content: { success: boolean; conversationId: number; tradeUid: string };
}

export interface NimSystemSendMsgReqMessage extends NimSystemBasicMessage {
  type: 'SEND_MSG_REQ';
  externalType: 'TOKOPEDIA';
  // 返回的时候带回来 标识唯一请求
  payload: {
    bizId: string;
    storeId: string;
    msgType: 'TEXT' | 'IMAGE';
    text: string;
    url: string;
  };
}
export interface NimSystemExportFileMessage extends NimSystemBasicMessage {
  type: 'EXPORT_FILE';
  content: {
    save: number;
    status: 'success' | 'failed';
    result?: string;
  };
}

export interface NimSystemTikTokShopOfflineMessage extends NimSystemBasicMessage {
  type: 'TIKTOK_SHOP_OFFLINE';
  content: {
    shopId: string;
    region: General.UpperCaseRegion;
  };
}

export interface NimSystemUpdateMarkMessage extends NimSystemBasicMessage {
  type: 'UPDATE_MARK';
  content: {
    conversationId: number;
    talkId: number;
    addMark: number;
    delMark: number;
  };
}

export interface NimSystemAuthSuccessMessage extends NimSystemBasicMessage {
  type: 'AUTH_SUCCESS';
  id: string;
}

export interface NimSystemKnowledgeQueryClearMessage extends NimSystemBasicMessage {
  type: 'KNOWLEDGE_QUERY_CLEAR';
  content: Conversation.TopBarInfo['knowledgeQuery'];
}

export interface NimSystemKnowledgeQueryMessage extends NimSystemBasicMessage {
  type: 'KNOWLEDGE_QUERY';
  content: Conversation.TopBarInfo['knowledgeQuery'];
}

export interface NimMessageContent {
  msgId: number;
  yunXinMsgId: Conversation.Message['yunXinMsgId'];
  success: boolean;
}

/**
 * 描述
 * @date 2021-08-28
 * @param {any} e:'message'
 * @param {any} cb:Nim.OnMsgCb
 * @returns {any} 卸载 : () => void
 */
function on(e: 'message', cb: (message: Conversation.Message) => void): Off;
function on(e: 'message', cb: (message: Resource.Message) => void): Off;
function on(e: 'connect', cb: NIMGetInstanceOptions['onconnect']): Off;
function on(e: 'disconnect', cb: NIMGetInstanceOptions['ondisconnect']): Off;
function on(e: 'willReconnect', cb: NIMGetInstanceOptions['onwillreconnect']): Off;
function on(e: 'error', cb: NIMGetInstanceOptions['onerror']): Off;
function on(e: 'sys:message:ack', cb: (p: NimSystemACKMessage) => void): Off;
function on(e: 'sys:message:ack_timeout', cb: (p: NimSystemACKTimeoutMessage) => void): Off;
function on(e: 'sys:message:assign_fail', cb: (p: NimSytemAssignFailMessage) => void): Off;
function on(e: 'sys:message:unread_count', cb: (p: NimSytemUnreadCountMessage) => void): Off;
function on(e: 'sys:message:read_event', cb: (p: NimSystemReadEventMessage) => void): Off;
function on(e: 'sys:message:follower_event', cb: (p: NimSystemFollowerEventMessage) => void): Off;
function on(e: 'sys:message:ai_reply', cb: (p: NimSystemAIReplyMessage) => void): Off;
function on(
  e: 'sys:message:official_read_mark',
  cb: (p: NimSystemOfficialReadMarkMessage) => void,
): Off;
function on(
  e: 'sys:message:assistant_read_mark',
  cb: (p: NimSystemAssistantReadMarkMessage) => void,
): Off;
function on(e: 'sys:message:remind_scene', cb: (p: NimSystemRemindSceneMessage) => void): Off;
function on(e: 'sys:message:remind_solve', cb: (p: NimSystemRemindSolveMessage) => void): Off;
function on(e: 'sys:message:store_transfer', cb: (p: NimSystemStoreTransferMessage) => void): Off;
function on(e: 'sys:message:pay', cb: (p: NimSystemPayMessage) => void): Off;
function on(e: 'sys:message:pull_notice_msg', cb: (p: NimSystemBasicMessage) => void): Off;
function on(
  e: 'sys:message:message_event_stream',
  cb: (p: NimSystemEventStreamMessage) => void,
): Off;
function on(
  e: 'sys:message:remove_talk_control',
  cb: (p: NimSystemRemoveTalkControlMessage) => void,
): Off;
function on(e: 'sys:message:batch_transfer', cb: (p: NimSystemBatchTransferMessage) => void): Off;
function on(
  e: 'sys:message:sync_tiktok_trade_uid',
  cb: (p: NimSystemSyncTiktokTradeUidMessage) => void,
): Off;
function on(e: 'sys:message:send_msg_req', cb: (p: NimSystemSendMsgReqMessage) => void): Off;

function on(e: 'sys:message:health_ack', cb: (p: NimSystemHealthACKMessage) => void): Off;

function on(e: 'sys:message:bot_sent_limit_reach', cb: (p: NimSystemBasicMessage) => void): Off;
function on(e: 'sys:message:sync_order_balance', cb: (p: NimSystemBasicMessage) => void): Off;

function on(
  e: 'sys:message:assign_conversation_stores_url',
  cb: (p: NimSystemBasicMessage) => void,
): Off;

function on(e: 'sys:message:sync_notice_unread_count', cb: (p: NimSystemBasicMessage) => void): Off;
function on(e: 'sys:message:export_file', cb: (p: NimSystemExportFileMessage) => void): Off;
function on(e: 'sys:message:sync_shop_info', cb: (p: NimSystemBasicMessage) => void): Off;
function on(
  e: 'sys:message:tiktok_shop_offline',
  cb: (p: NimSystemTikTokShopOfflineMessage) => void,
): Off;
function on(e: 'sys:message:update_mark', cb: (p: NimSystemUpdateMarkMessage) => void): Off;
function on(e: 'sys:message:auth_success', cb: (p: NimSystemAuthSuccessMessage) => void): Off;
function on(
  e: 'sys:message:knowledge_query_clear',
  cb: (p: NimSystemKnowledgeQueryClearMessage) => void,
): Off;
function on(e: 'sys:message:knowledge_query', cb: (p: NimSystemKnowledgeQueryMessage) => void): Off;

function on(e: typeof NIM_LOGIN_SIGNAL, cb: (p: InstanceOptions) => any): Off;

function on(e: Events | typeof NIM_LOGIN_SIGNAL, cb: any) {
  nimitt.on(e, cb);
  return () => nimitt.off(e, cb);
}

export type EmitEvents =
  | 'message'
  | 'connect'
  | 'disconnect'
  | 'error'
  | 'willReconnect'
  | `sys:message:${Lowercase<NimSystemMessageType>}`;

/** 消息(包括系统消息) */
function emit(e: 'message', message: Conversation.Message): void;
function emit(
  e: 'connect',
  param: Parameters<NonNullable<NIMGetInstanceOptions['onconnect']>>[0],
): void;
function emit(
  e: 'disconnect',
  param: Parameters<NonNullable<NIMGetInstanceOptions['ondisconnect']>>[0],
): void;
function emit(
  e: 'willReconnect',
  param: Parameters<NonNullable<NIMGetInstanceOptions['onwillreconnect']>>[0],
): void;
// #region 系统通知
function emit(e: 'sys:message:health_ack', param: NimSystemHealthACKMessage): void;
function emit(e: 'sys:message:ack', param: NimSystemACKMessage): void;
function emit(e: 'sys:message:ack_timeout', param: NimSystemACKTimeoutMessage): void;
function emit(e: 'sys:message:assign_fail', param: NimSytemAssignFailMessage): void;
function emit(e: 'sys:message:unread_count', param: NimSytemUnreadCountMessage): void;
function emit(e: 'sys:message:read_event', param: NimSystemReadEventMessage): void;
function emit(e: 'sys:message:follower_event', param: NimSystemFollowerEventMessage): void;
function emit(e: 'sys:message:ai_reply', param: NimSystemAIReplyMessage): void;
function emit(e: 'sys:message:official_read_mark', param: NimSystemOfficialReadMarkMessage): void;
function emit(e: 'sys:message:assistant_read_mark', param: NimSystemAssistantReadMarkMessage): void;
function emit(e: 'sys:message:remind_scene', param: NimSystemRemindSceneMessage): void;
function emit(e: 'sys:message:remind_solve', param: NimSystemRemindSolveMessage): void;
function emit(e: 'sys:message:store_transfer', param: NimSystemStoreTransferMessage): void;
function emit(e: 'sys:message:pay', param: NimSystemPayMessage): void;
function emit(e: 'sys:message:pull_notice_msg', param: NimSystemBasicMessage): void;
function emit(e: 'sys:message:message_event_stream', param: NimSystemEventStreamMessage): void;
function emit(e: 'sys:message:remove_talk_control', param: NimSystemRemoveTalkControlMessage): void;
function emit(e: 'sys:message:batch_transfer', param: NimSystemBatchTransferMessage): void;
function emit(
  e: 'sys:message:sync_tiktok_trade_uid',
  param: NimSystemSyncTiktokTradeUidMessage,
): void;
function emit(e: 'sys:message:send_msg_req', params: NimSystemSendMsgReqMessage): void;
function emit(e: 'sys:message:bot_sent_limit_reach', params: NimSystemBasicMessage): void;
function emit(e: 'sys:message:sync_order_balance', params: NimSystemBasicMessage): void;
function emit(e: 'sys:message:assign_conversation_stores_url', params: NimSystemBasicMessage): void;
function emit(e: 'sys:message:sync_notice_unread_count', params: NimSystemBasicMessage): void;
function emit(e: 'sys:message:export_file', params: NimSystemExportFileMessage): void;
function emit(e: 'sys:message:sync_shop_info', params: NimSystemBasicMessage): void;
function emit(
  e: 'sys:message:tiktok_shop_offline',
  params: NimSystemTikTokShopOfflineMessage,
): void;
function emit(e: 'sys:message:update_mark', params: NimSystemUpdateMarkMessage): void;
function emit(e: 'sys:message:auth_success', params: NimSystemAuthSuccessMessage): void;
function emit(
  e: 'sys:message:knowledge_query_clear',
  params: NimSystemKnowledgeQueryClearMessage,
): void;
function emit(e: 'sys:message:knowledge_query', params: NimSystemKnowledgeQueryMessage): void;

// #endregion
function emit(
  e: 'error',
  param: Parameters<NonNullable<NIMGetInstanceOptions['onerror']>>[0],
): void;

function emit(e: EmitEvents, p: any) {
  nimitt.emit(e, p);
}

export { emit, on };
